// --------------------------
// IMPORTS
// --------------------------
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useAuth } from "./crm/hooks/useAuth";
import { useUIHelpers } from "./crm/hooks/useUIHelpers"; // Import the UI helpers hook
import { componentMap, roles } from "./crm/utils/routes";
import NotFound from "./crm/views/Layouts/NotFound";
import UnderMaintenance from "./crm/views/Layouts/UnderMaintenance";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";

import LogoutHandler from "./crm/views/LogoutHandler";
import { resetConnectionErrorFlag } from "./crm/utils/APICaller";
import CustomLoader from "./crm/utils/CustomLoader";
import {
  //checkActiveDelegationThunk,
  ercountThunk,
  ncountThunk,
  validateTokenThunk,
} from "./crm/store/thunks/authThunk";
import { clearRedux } from "./crm/store/slices/authSlice";

import {
  getProjectCodeListThunk,
  getExpenseCodeListThunk,
  getCurrencyListThunk,
  getDepartmentListThunk,
} from "./crm/store/thunks/masterDataThunk";
import LogViewer from "./crm/views/LogViewer";

// --------------------------
// MAIN COMPONENT
// --------------------------
const App = () => {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname?.split("/");

  const originalUser = useSelector((state) => state.auth.originalUser);
  // Use the custom hook for authentication and UI helpers
  const { loading, isLoggedIn, userRole, accessToken } = useAuth(
    navigate,
    location,
  );

  // --------------------------
  // REDUX STATE
  // --------------------------
  const storeRole = useSelector((state) => state.auth.role);
  const storeToken = useSelector((state) => state.auth.accessToken);
  const storeLIUId = useSelector((state) => state.auth.lIUId);
  const storeAccessTokenExpiresOn = useSelector(
    (state) => state.auth.accessTokenExpiresOn,
  );
  const { expenseCodes, projectCodes, currencies, departments } = useSelector(
    (state) => state.masterData,
  );

  const isDelegated = useSelector((state) => state.auth.isDelegated);
  const actingUser = useSelector((state) => state.auth.actingUser);

  // --------------------------
  // LOCAL STATES
  // --------------------------
  // Maintenance Mode Flag (You could replace this with a global state or an API call)
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);
  const [serverStatus, setServerStatus] = useState("up");
  const [isCheckingServerStatus, setIsCheckingServerStatus] = useState(true); // Flag to indicate if server status is being checked

  const navigateHome = () => {
    navigate("/dashboard");
  };

  // --------------------------
  // LOCAL VARIABLES
  // --------------------------
  const effectiveRole = isDelegated && actingUser ? actingUser.role : storeRole;

  const effectiveLIUId =
    isDelegated && actingUser ? actingUser.lIUId : storeLIUId;

  // Get the accessible routes based on the user's role
  const hasAccess = (role) => roles[role] || [];
  const accessibleRoutes = effectiveRole ? hasAccess(effectiveRole) : [];

  // --------------------------
  // API CALLS
  // --------------------------

  const expenseRequestsCount = () => {
    dispatch(
      ercountThunk({
        role: effectiveRole,
        lIUId: effectiveLIUId,
        token: storeToken,
        accessTokenExpiresOn: storeAccessTokenExpiresOn,
      }),
    );
  };

  const notificationCount = () => {
    dispatch(
      ncountThunk({
        role: effectiveRole,
        lIUId: effectiveLIUId,
        token: storeToken,
        accessTokenExpiresOn: storeAccessTokenExpiresOn,
      }),
    );
  };

  // Function to check server status (whether the backend is up or not)
  const checkMaintenanceStatus = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BACKEND + "/checkServerStatus",
      );
      setServerStatus(response.data.status);

      // If the server is down or in maintenance, navigate to the /underMaintenance route
      if (response.data.status === "Error") {
        setIsUnderMaintenance(true);
      } else {
        setIsUnderMaintenance(false);
      }
    } catch (error) {
      setServerStatus("Error");
      setIsUnderMaintenance(true);
    } finally {
      setIsCheckingServerStatus(false); // Finished checking server status
    }
  };

  const clearAndLogout = async () => {
    dispatch(clearRedux());
    localStorage.setItem("logout", Date.now()); // Triggers storage event
    window.location.href = process.env.REACT_APP_SSO_LOGIN_REDIRECT_URI;
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token"); // Get the token from URL

    if (token) {
      dispatch(validateTokenThunk(token)).then((res) => {
        if (validateTokenThunk.fulfilled.match(res)) {
          if (!isLoggedIn) {
            navigate("/dashboard", { replace: true });
          }
        } else {
          clearAndLogout();
        }
      });
    } else {
      const logoutReq = pathName[1] === "logout" ? true : false;

      if (!accessToken && !logoutReq) {
        window.location.href = process.env.REACT_APP_SSO_LOGIN_REDIRECT_URI;
      }
      if (location.pathname === "/" && accessToken) {
        navigate("/dashboard", { replace: true });
      }
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && serverStatus === "Server is running") {
      // Run the function immediately
      if (effectiveRole !== process.env.REACT_APP_EROLE) {
        expenseRequestsCount();
        notificationCount();
      }
      const intervalId = setInterval(() => {
        if (
          effectiveRole !== process.env.REACT_APP_EROLE &&
          serverStatus === "Server is running"
        ) {
          expenseRequestsCount();
          notificationCount();
        }
      }, 5000);
      return () => clearInterval(intervalId);
    }
  }, [effectiveRole, storeToken, isLoggedIn, serverStatus]);

  // useEffect(() => {
  //   if (isLoggedIn && storeLIUId) {

  //     dispatch(checkActiveDelegationThunk(storeLIUId));
  //   }
  // }, [isLoggedIn, storeLIUId]);

  const delegatedAccessVersion = useSelector(
    (state) => state.delegateAccess.delegatedAccessVersion,
  );

  // useEffect(() => {
  //   if (!isLoggedIn || !storeLIUId) return;

  //   dispatch(checkActiveDelegationThunk(storeLIUId));
  // }, [dispatch, isLoggedIn, storeLIUId, delegatedAccessVersion]);

  // const authReady = isLoggedIn && !!storeLIUId;
  // const delegationChecked = useRef(false);

  // useEffect(() => {
  //   if (!authReady || delegationChecked.current) return;
  //   delegationChecked.current = true;

  //   dispatch(checkActiveDelegationThunk(originalUser?.lIUId));
  // }, [authReady]);

  // Call the UI helpers hook
  useUIHelpers();

  // Run once on mount
  useEffect(() => {
    checkMaintenanceStatus();
  }, []);

  // Continuous health check every 10 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      checkMaintenanceStatus();
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (serverStatus === "Server is running") {
      resetConnectionErrorFlag();
    }
  }, [serverStatus]);

  useEffect(() => {
    if (!isUnderMaintenance && location.pathname === "/underMaintenance") {
      document.title = process.env.REACT_APP_NAME;
      navigate("/", { replace: true });
    }
  }, [isUnderMaintenance]);

  useEffect(() => {
    if (!expenseCodes?.length) dispatch(getExpenseCodeListThunk());
    if (!projectCodes?.length) dispatch(getProjectCodeListThunk());
    if (!currencies?.length) dispatch(getCurrencyListThunk());
    if (!departments?.length) dispatch(getDepartmentListThunk());
  }, [dispatch, expenseCodes, projectCodes, currencies, departments]);

  // Show loading while the server status is being checked
  if (isCheckingServerStatus) {
    return <CustomLoader showLoader="true" />; // Show loading until server status is confirmed
  }

  if (isUnderMaintenance) {
    document.title = "Under Maintenance"; // You can replace this with any desired title
    return (
      <>
        <UnderMaintenance />
        <Navigate to="/underMaintenance" replace />
      </>
    );
  }

  // --------------------------
  // HANDLERS
  // --------------------------

  //To show notification using Ant Design's notification component
  const showNotification = (errorMsg, moreThan = "no") => {
    message.error({
      content: (
        <div style={{ whiteSpace: "pre-line" }}>
          {moreThan === "yes" ? (
            <strong>Errors occurred:</strong>
          ) : (
            <strong>An error occurred:</strong>
          )}
          <p>{errorMsg}</p> {/* This ensures line breaks are respected */}
        </div>
      ),
      duration: 5, // duration in seconds
      className: "error-message", // Add any custom styles you need
    });
  };

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <Routes>
      <Route index element={isLoggedIn && componentMap["/dashboard"]} />
      {accessibleRoutes.length === 0 ? (
        <Route path="*" element={<Navigate to="/" replace />} />
      ) : (
        accessibleRoutes.map((route) => (
          <Route key={route} path={route} element={componentMap[route]} />
        ))
      )}
      <Route path="/logs" element={<LogViewer />} />
      <Route path="/logout/:empID" element={<LogoutHandler />} />
      <Route
        path="*"
        element={<Navigate to={componentMap.noRoute} replace />}
      />
      <Route path="/404" element={<NotFound />} />
    </Routes>
  );
};

export default App;
